/**
 * Version Control Service for PWA
 * Handles app version tracking, update detection, and service worker management
 */

export interface AppVersion {
  version: string;
  buildTime: string;
  gitCommit?: string;
}

export interface UpdateInfo {
  hasUpdate: boolean;
  currentVersion: string;
  newVersion?: string;
  updateAvailable: boolean;
}

class VersionService {
  private static instance: VersionService;
  private currentVersion: AppVersion;
  private updateCheckInterval: NodeJS.Timeout | null = null;
  // The last deployed version we already fired an update event for, so the 30-min poll doesn't
  // re-pop the (dismissible) update toast every cycle for the same release.
  private lastNotifiedVersion: string | null = null;

  private constructor() {
    // Get version from environment variables injected at build time
    this.currentVersion = {
      version: process.env.REACT_APP_VERSION ?? "1.0.0",
      buildTime: process.env.REACT_APP_BUILD_TIME ?? new Date().toISOString(),
      gitCommit: process.env.REACT_APP_GIT_COMMIT,
    };
  }

  public static getInstance(): VersionService {
    if (!VersionService.instance) {
      VersionService.instance = new VersionService();
    }
    return VersionService.instance;
  }

  /**
   * Get current app version information
   */
  public getCurrentVersion(): AppVersion {
    return { ...this.currentVersion };
  }

  /**
   * Initialize version control system
   */
  public async initialize(): Promise<void> {
    console.log(`[VersionService] Initialized - Version: ${this.currentVersion.version}`);
    // Detect a new release by comparing the running (baked) version against the deployed
    // /version.json. Check once now, then poll periodically. Detection is deliberately independent
    // of the service worker's waiting/skipWaiting behavior.
    await this.checkForUpdates();
    this.startPeriodicUpdateCheck();
  }

  /**
   * Fetch the deployed build's version.json (bypassing all caches). Returns null if unavailable.
   */
  private async fetchDeployedVersion(): Promise<AppVersion | null> {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return null;
      return (await res.json()) as AppVersion;
    } catch (error) {
      console.error("[VersionService] Error fetching deployed version:", error);
      return null;
    }
  }

  /**
   * Check for app updates by comparing the running version against the deployed /version.json.
   * An update is available when the deployed version differs from the one baked into this bundle.
   */
  public async checkForUpdates(): Promise<UpdateInfo> {
    const currentVersion = this.currentVersion.version;
    const deployed = await this.fetchDeployedVersion();

    if (deployed?.version && deployed.version !== currentVersion) {
      // Only dispatch the event once per new deployed version — the periodic poll would otherwise
      // re-fire it every 30 min and re-pop the dismissed toast. The return value is always fresh,
      // so the Settings modal's manual check is unaffected.
      if (deployed.version !== this.lastNotifiedVersion) {
        this.lastNotifiedVersion = deployed.version;
        console.log(`[VersionService] New version available: ${currentVersion} -> ${deployed.version}`);
        this.notifyUpdateAvailable(deployed.version);
      }
      return { hasUpdate: true, currentVersion, newVersion: deployed.version, updateAvailable: true };
    }

    return { hasUpdate: false, currentVersion, updateAvailable: false };
  }

  /**
   * Apply pending update. Always ends in a page reload so the caller's "Updating…" state can never
   * hang: prefer activating a waiting worker (postMessage SKIP_WAITING → reload on controllerchange),
   * but when there is no waiting worker (the SW is built with skipWaiting:true, so a new worker
   * usually auto-activates rather than waiting) or the controllerchange never fires, reload anyway.
   */
  public async applyUpdate(): Promise<void> {
    // Reload exactly once, whichever path triggers first.
    let reloaded = false;
    const reload = () => {
      if (reloaded) return;
      reloaded = true;
      console.log("[VersionService] Reloading to apply update");
      window.location.reload();
    };

    try {
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration?.waiting) {
        console.log("[VersionService] Applying update via waiting worker...");
        navigator.serviceWorker.addEventListener("controllerchange", reload);
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        // Fallback: the generated SW may not handle SKIP_WAITING, or it may have already
        // auto-activated — reload regardless so the button never spins forever.
        setTimeout(reload, 2000);
      } else {
        // No waiting worker (already activated, or none registered). Reload to pick up the
        // freshly-activated worker and its precached assets.
        reload();
      }
    } catch (error) {
      console.error("[VersionService] Error applying update:", error);
      reload(); // last resort — never leave the caller stuck
    }
  }

  /**
   * Start periodic update checks
   */
  private startPeriodicUpdateCheck(): void {
    // Check for updates every 30 minutes
    this.updateCheckInterval = setInterval(
      async () => {
        console.log("[VersionService] Performing periodic update check");
        await this.checkForUpdates();
      },
      30 * 60 * 1000,
    );
  }

  /**
   * Stop periodic update checks
   */
  public stopPeriodicUpdateCheck(): void {
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
      this.updateCheckInterval = null;
    }
  }

  /**
   * Notify app that an update is available. Carries both the running and the newly-deployed
   * version so listeners (e.g. UpdateNotification) can show "1.0.0 → 1.0.1".
   */
  private notifyUpdateAvailable(newVersion?: string): void {
    const event = new CustomEvent("app-update-available", {
      detail: {
        currentVersion: this.currentVersion.version,
        newVersion,
        updateAvailable: true,
      },
    });
    window.dispatchEvent(event);
  }

  /**
   * Get formatted version string for display
   */
  public getVersionString(): string {
    const { version, buildTime, gitCommit } = this.currentVersion;
    const buildDate = new Date(buildTime).toLocaleDateString();

    if (gitCommit) {
      return `v${version} (${buildDate} - ${gitCommit.substring(0, 7)})`;
    }

    return `v${version} (${buildDate})`;
  }

  /**
   * Force refresh the application
   */
  public forceRefresh(): void {
    console.log("[VersionService] Force refreshing application");
    window.location.reload();
  }

  /**
   * Clean up resources
   */
  public cleanup(): void {
    this.stopPeriodicUpdateCheck();
  }
}

export default VersionService;
